// 브라우저 시계 동기 — 내 시계가 서버보다 얼마나 어긋나 있는가
//
// 이 제품의 표시 시각 편차 예산이 p95 250밀리초이고(2026-10-01 에 200에서 올렸다), 그 편차를 재는 코드가 곧 상단
// 고정줄의 ±밀리초 표시다. 계측 프로브의 숫자가 나중에 버려지는 것이 아니라 그대로
// 제품의 한 줄이 되는 이유가 그것이다.
//
// **왕복이 커도 상하행이 대칭이면 오차는 작다.** 이 문장이 이 파일의 설계 근거 전부다.
// 편차를 흐리는 것은 왕복의 크기가 아니라 가는 길과 오는 길의 **비대칭**이고, 비대칭의
// 절반이 그대로 오차가 된다. 그래서 서울 한 곳에 서버를 두고 남아공에서 접속해도
// 되지만, 대신 비대칭이 큰 표본을 골라내는 일에 이 파일의 대부분이 쓰인다. 방법은
// 둘이다 — 왕복이 짧은 표본만 남기고(짧은 왕복은 비대칭이 클 수 없다), 그중에서
// 중앙값을 고른다(한 표본이 통째로 튀어도 중앙값은 끌려가지 않는다).
//
// 시각을 직접 읽지 않고 시계를 주입받는다. 그래야 인위적으로 주입한 skew 와 추정값을
// 대조하는 검증이 성립한다 — 자기 추정기로 자기 추정기를 재면 아무것도 검증되지 않는다.

import {
  addDuration,
  duration,
  type Duration,
  type Instant,
  toMillis,
} from '../../core/time/brand.ts'
import type { Clock } from '../../core/time/clock.ts'
import type { ClockSyncResponse } from '../../contract/schema.ts'
import { routes } from '../../contract/routes.ts'

/**
 * 왕복 한 번의 네 타임스탬프. t0 은 브라우저가 보낸 순간, t1 은 서버가 받은 순간,
 * t2 는 서버가 답한 순간, t3 은 브라우저가 받은 순간이다. t0·t3 은 내 시계 위의 값이고
 * t1·t2 는 서버 시계 위의 값이다 — 두 시계가 다르다는 것이 재려는 대상 그 자체다.
 */
export interface RoundTrip {
  t0: number
  t1: number
  t2: number
  t3: number
}

export interface RoundTripReading {
  /** 서버 시각 − 내 시각. 양수면 서버가 앞서 있다. */
  offsetMs: number
  /** 네트워크에 실제로 쓰인 시간. 서버가 답하는 데 쓴 시간은 빠져 있다. */
  roundTripMs: number
}

export function readRoundTrip(trip: RoundTrip): RoundTripReading {
  const { t0, t1, t2, t3 } = trip
  if (t3 < t0) {
    throw new RangeError(`왕복 표본이 거꾸로다: 보낸 시각 ${String(t0)} > 받은 시각 ${String(t3)}`)
  }
  if (t2 < t1) {
    throw new RangeError(
      `서버 타임스탬프가 거꾸로다: 받은 시각 ${String(t1)} > 답한 시각 ${String(t2)}`,
    )
  }

  // 왕복에서 서버가 답하는 데 쓴 시간(t2 − t1)을 뺀다. 서버가 느린 것과 네트워크가 먼
  // 것은 다른 사건이라, 섞어 두면 이벤트 루프가 밀린 순간의 표본이 "먼 표본"으로
  // 오해받아 걸러진다.
  const roundTripMs = t3 - t0 - (t2 - t1)
  if (roundTripMs < 0) {
    throw new RangeError(`왕복이 음수다 (${String(roundTripMs)}ms) — 표본이 신뢰할 수 없다`)
  }

  // 네 값이 전부 정수라도 두 항의 합은 홀수일 수 있어 0.5가 남는다. 이 제품의 시간
  // 규약이 정수 밀리초이므로 여기서 한 번만 반올림한다.
  const offsetMs = Math.round((t1 - t0 + (t2 - t3)) / 2)
  return { offsetMs, roundTripMs }
}

export interface OffsetEstimate {
  /** 서버 시각 − 내 시각. 화면의 ±밀리초가 이 값이다. */
  offsetMs: Duration
  /** 추정에 채택한 표본 중 가장 짧은 왕복. */
  roundTripMs: Duration
  /** 추정에 실제로 쓴 표본 수. */
  usedCount: number
  /** 지금 창에 모여 있는 표본 수. */
  observedCount: number
}

/**
 * 표본 여럿에서 편차 하나를 고른다.
 *
 * 평균을 쓰지 않는 이유: 혼잡한 구간에 걸린 표본 하나가 왕복 3초에 편차 2초로 튀는데,
 * 평균은 그 하나에 통째로 끌려간다. 중앙값은 끌려가지 않는다. 그리고 중앙값을 내기
 * 전에 왕복이 긴 표본부터 버린다 — 왕복이 짧으면 비대칭도 클 수 없어서, 짧은 표본만
 * 남기는 것이 곧 비대칭이 작은 표본만 남기는 것이다.
 */
export function estimateOffset(trips: readonly RoundTrip[], keepBest = 4): OffsetEstimate | null {
  if (trips.length === 0) return null

  const readings = trips.map(readRoundTrip).sort((a, b) => a.roundTripMs - b.roundTripMs)
  const keep = Math.max(1, Math.min(keepBest, readings.length))
  const used = readings.slice(0, keep)

  const offsets = used.map((r) => r.offsetMs).sort((a, b) => a - b)
  const mid = offsets.length >> 1
  const median =
    offsets.length % 2 === 1
      ? offsets[mid]!
      : Math.round((offsets[mid - 1]! + offsets[mid]!) / 2)

  return {
    offsetMs: duration(median),
    roundTripMs: duration(used[0]!.roundTripMs),
    usedCount: used.length,
    observedCount: readings.length,
  }
}

/**
 * 화면이 편차 칸을 그릴 때 갈라야 하는 세 상태.
 *
 * 전에는 이 셋이 `estimate === null` 하나로 뭉쳐 있었다. 표본이 모자란 것과, 왕복이
 * 죽은 것과, 시계가 뛴 것이 화면에서 전부 같은 「측정 중 (n/3)」이었다. 사람이 할 일이
 * 셋 다 다른데(기다린다 / 연결을 본다 / 못 믿는다) 화면은 하나만 말했다.
 */
export type SkewState =
  /** 값이 지금 것이고 믿어도 된다. */
  | 'live'
  /** 표본을 다시 모으는 중. 마지막 확정값이 있으면 그것을 흐리게 곁들인다. */
  | 'measuring'
  /** 문턱을 넘도록 성공 왕복이 없다. 재는 중이 아니라 못 재는 중이다. */
  | 'offline'

/** 화면이 편차 칸 하나를 그리는 데 필요한 전부. 한 소스에서 한 번에 읽는다. */
export interface SkewView {
  /**
   * 화면에 그릴 밀리초. `live` 면 지금 값이고, 아니면 마지막 확정값이다.
   * 들고 있는 값조차 없으면 null 이고 그때는 숫자 자리가 아예 비어야 한다.
   */
  millis: number | null
  state: SkewState
  /** 「측정 중 (2/3)」의 괄호 안. `measuring` 일 때만 뜻이 있다. */
  progress: string
}

/** 한 시점의 두 축을 함께 찍은 것. 불연속 감지는 이 둘의 차이를 본다. */
export interface ClockMark {
  wall: Instant
  elapsed: Duration
}

/**
 * 두 시점 사이에 **벽시계가 단조 시계보다 얼마나 더 갔는가.**
 *
 * 기기가 자는 동안 단조 시계는 멈추고 벽시계는 계속 간다. 그 차이가 곧 잔 시간이다.
 * 화면 꺼짐 방지를 못 쓰는 것이 이 제품의 확정 사실이라 화면 꺼짐은 예외가 아니라
 * 상시 조건이고, 그래서 이 검사는 드물게 도는 안전장치가 아니라 상시 경로다.
 */
export function continuityGapMs(before: ClockMark, after: ClockMark): Duration {
  const wallDelta = toMillis(after.wall) - toMillis(before.wall)
  const elapsedDelta = toMillis(after.elapsed) - toMillis(before.elapsed)
  return duration(wallDelta - elapsedDelta)
}

export interface ContinuityCheck {
  discontinuous: boolean
  gapMs: Duration
}

export type ClockSyncTransport = (t0: number) => Promise<ClockSyncResponse>

export interface ClockSyncOptions {
  clock: Clock
  transport: ClockSyncTransport
  /** 창에 유지할 표본 수. 넘치면 가장 오래된 것부터 버린다. */
  windowSize?: number
  /** 창 안에서 추정에 쓸, 왕복이 가장 짧은 표본 수. */
  keepBest?: number
  /**
   * 값을 내놓기 전에 모아야 할 최소 표본 수.
   *
   * 표본 하나에서 나온 값은 비대칭을 한 번도 걸러 내지 못한 값이다. 그 하나가 혼잡한
   * 구간에 걸렸으면 화면의 ±밀리초가 통째로 거짓이 되는데, 화면은 그것을 확신에 찬
   * 숫자로 보여 준다. 최소치를 넘기 전까지 「측정 중」을 띄우는 편이 정직하다.
   */
  minSamples?: number
  /**
   * 두 축의 어긋남이 이 폭을 넘으면 그동안 기기가 잤다고 보고 다시 수렴시킨다.
   *
   * **기본값 250 은 정본의 편차 목표와 같은 숫자다** (2026-10-01 사용자 결정으로 목표를 이 값에
   * 맞췄다). 이 폭 아래의 점프는 오차 범위로 보고 넘어가며, 표본 창이 새 값으로 바뀔 때까지
   * 화면이 그 폭만큼 늦게 따라가는 것을 판정 계약이 허용한다. 이 값을 바꾸면 요구사항의 시간 절과
   * 판정 계약의 시계 편차 케이스도 함께 바꾼다.
   */
  resyncToleranceMs?: number
  /**
   * 마지막 성공 왕복이 이만큼 지나면 값을 주장하지 않는다.
   *
   * **드리프트에서 역산하지 않았고 그 이유를 적어 둔다.** 표시 오차 목표 250밀리초를 소비자
   * 수정 진동자의 드리프트(대략 20~100 ppm)로 나누면 40분 넘게 나오는데, 이 결함은 값이
   * 늙어서 생기는 것이 아니라 **시계가 뛰어서** 생긴다. 그런 폭으로는 아무것도 못 막는다.
   *
   * 그래서 기준을 바꿨다 — 「값이 늙었다」가 아니라 **「재는 일이 멈췄다」**다. 왕복은 5초마다
   * 도므로 30초면 여섯 번 연속 실패한 것이고, 그것은 잡음이 아니라 상태다. 그 상태에서 옛
   * 값을 확신에 차서 유지하면 화면이 「모른다」를 「0 밀리초 어긋나 있다」로 말하게 된다.
   */
  staleAfterMs?: number
  /**
   * 성공 왕복 없이 이만큼 지나면 **「재는 중」이 아니라 「못 재는 중」으로 부른다.**
   *
   * 위의 낡음 문턱과 재는 것이 겹쳐 보이지만 답하는 물음이 다르다. 낡음은 「이 값을
   * 아직 주장해도 되는가」를 묻고, 이것은 **「사람에게 뭐라고 말할 것인가」**를 묻는다.
   * 재는 중인 것과 못 재는 중인 것은 사람이 할 일이 달라서(기다린다 대 연결을 본다)
   * 같은 이름을 붙이면 안 되고, 그 갈림이 값을 거두는 시점보다 먼저 와야 쓸모가 있다.
   *
   * 15초인 이유는 폴링이 5초 간격이기 때문이다. 한 번 놓친 것은 잡음이고 세 번 연속은
   * 상태다. 낡음의 30초를 그대로 쓰면 나가는 길이 막힌 뒤 30초 동안 화면이 「측정 중」
   * 이라고 말하는데, 그 말은 「곧 나온다」로 읽혀서 사람이 아무것도 안 하게 만든다.
   *
   * **마지막 성공이 아니라 마지막 성공 「또는 시작」부터 센다.** 첫 왕복부터 죽는 경로가
   * 있고, 마지막 성공만으로 세면 그 경로는 문턱을 영영 못 넘어 무한히 「측정 중」이 된다.
   */
  offlineAfterMs?: number
  /**
   * 새 표본이 지금 쓰는 값과 이만큼 어긋나면 **모아 둔 것을 버리고 다시 수렴한다.**
   *
   * 버리지 않으면 새 표본이 옛 표본과 창 안에서 섞이고, 중앙값이 참값의 절반쯤 되는 값을
   * 낸다 — 다섯 자리까지 정밀해 보이는 모양이라 사람이 의심할 단서가 없다. 2026-08-17
   * 대리 관측이 45초 점프에서 `−22500 ms` 를 실제로 관측했다.
   *
   * **한 표본만으로 버린다.** 두 번 연속을 기다리면 그동안 화면이 확신에 찬 틀린 값을
   * 유지하는데, 잃는 것은 「측정 중」으로 10초 남짓 돌아가는 것뿐이다. 이 모듈의 전제가
   * 「모르는 것을 모른다고 표시하는 편이 낫다」이고 여기가 그 전제를 그대로 따르는 자리다.
   */
  divergenceToleranceMs?: number
}

/**
 * 편차를 지속 추정한다. 표본을 창에 모으고, 왕복이 짧은 것만 골라, 중앙값을 낸다.
 *
 * 화면 복귀마다 `checkContinuity()` 를 부른다. 어긋남이 허용 폭을 넘으면 **모아 둔
 * 표본을 통째로 버린다** — 자는 동안 서버와의 관계가 달라졌을 수 있는데, 낡은 표본을
 * 섞으면 틀린 값을 자신 있게 표시하게 된다. 모르는 것을 모른다고 표시하는 편이 낫다.
 */
export class ClockSync {
  readonly #clock: Clock
  readonly #transport: ClockSyncTransport
  readonly #windowSize: number
  readonly #keepBest: number
  readonly #minSamples: number
  readonly #resyncToleranceMs: number
  readonly #staleAfterMs: number
  readonly #offlineAfterMs: number
  readonly #divergenceToleranceMs: number
  /** 이 인스턴스가 생긴 단조 시각. 「한 번도 성공한 적 없음」의 기준점이다. */
  readonly #startedElapsed: number

  #trips: RoundTrip[] = []
  #mark: ClockMark
  /**
   * **폭 안이라 넘어간 점프의 기록.** 단조 시각과 점프 폭의 짝이다 (2026-10-01).
   *
   * 폭 안의 점프는 오차 범위로 보고 넘어가고, 표본 창이 새 값으로 바뀔 때까지 화면에 남는다
   * (사용자 결정, 그 대가가 「최대 0.25초 틀린 숫자」다). 그런데 같은 날 교차 확인자가 200ms
   * 점프 둘이 한 왕복씩 떨어져 오면 둘 다 넘어가 화면이 400ms 틀린 채 남는 것을 잡았다. 그 한도를
   * 지키려고, 창이 갈리기 전의 점프를 모아 합으로 본다.
   */
  #recentJumps: Array<{ elapsedMs: number; gapMs: number }> = []
  #lastEventLoopLagMs: number | null = null
  /** 마지막으로 성공한 왕복의 **단조** 시각. 벽시계로 재면 점프가 이 값을 오염시킨다. */
  #lastSuccessElapsed: number | null = null
  /**
   * 마지막으로 **서버가 답한** 단조 시각. 위의 것과 가른 이유는 답하는 물음이 달라서다.
   * 위는 「이 표본 창이 낡았는가」를 묻고 `reset()` 이 지우지만, 이것은 「지금 서버에
   * 닿는가」를 묻는 값이라 표본을 버리는 일과 무관하다. 하나로 합치면 표본을 버릴 때마다
   * 화면이 「연결 없음」으로 튄다.
   */
  #lastReachableElapsed: number | null = null
  /**
   * 마지막으로 확정된 추정. 화면이 다시 재는 동안 흐리게 붙여 두는 값이다.
   *
   * **버리는 조건이 한 줄이다: 두 시계 중 하나가 뛰었다고 판단한 순간.** 표본이 낡거나
   * 모자란 것은 값이 틀렸다는 뜻이 아니라 다시 재는 중이라는 뜻이라, 그때 지우면 화면이
   * 참인 값을 감춘다 (2026-08-27 사용자 결정).
   */
  #lastEstimate: OffsetEstimate | null = null

  constructor(options: ClockSyncOptions) {
    this.#clock = options.clock
    this.#transport = options.transport
    this.#windowSize = options.windowSize ?? 8
    this.#keepBest = options.keepBest ?? 4
    this.#minSamples = options.minSamples ?? 3
    this.#resyncToleranceMs = options.resyncToleranceMs ?? 250
    this.#staleAfterMs = options.staleAfterMs ?? 30_000
    this.#offlineAfterMs = options.offlineAfterMs ?? 15_000
    this.#divergenceToleranceMs = options.divergenceToleranceMs ?? 250
    this.#mark = { wall: options.clock.now(), elapsed: options.clock.elapsed() }
    this.#startedElapsed = toMillis(options.clock.elapsed())
  }

  async probe(): Promise<RoundTripReading> {
    // **왕복마다 두 축을 견준다.** 전에는 화면 복귀에서만 봤는데, 화면을 보고 있는 동안
    // 벽시계가 뛰면(NTP 보정이 정확히 그 모양이다) 아무도 그것을 보지 않았다. 그 구간에서
    // 화면은 틀린 시각을 그리면서 같은 자리에서 「0 밀리초 어긋나 있다」고 말했다.
    this.checkContinuity()

    const t0 = toMillis(this.#clock.now())
    const response = await this.#transport(t0)
    const t3 = toMillis(this.#clock.now())

    if (response.t0 !== t0) {
      throw new RangeError(
        `서버가 다른 t0 을 돌려줬다 (보낸 값 ${String(t0)}, 받은 값 ${String(response.t0)}). ` +
          '이 표본은 내 요청의 답이 아니므로 버린다.',
      )
    }

    const trip: RoundTrip = { t0, t1: response.t1, t2: response.t2, t3 }
    const reading = readRoundTrip(trip)

    // **낡아 있던 창은 되살리지 않는다.** 재는 일이 30초 넘게 멈춰 있었다면 그 사이에
    // 무슨 일이 있었는지 이 모듈은 모른다. 첫 성공 왕복 하나로 옛 표본 여섯을 다시
    // 믿기 시작하면, 「모른다」에서 「확신」으로 한 걸음에 건너뛰는 셈이 된다.
    if (this.#trips.length > 0 && this.#usable().length === 0) this.reset()

    // **새 표본이 지금 쓰는 값과 크게 어긋나면 모아 둔 것을 버린다.** 섞으면 중앙값이
    // 두 세계의 중간값을 내는데, 그 값은 어느 쪽도 아니면서 정밀해 보인다.
    const held = this.estimate
    if (
      held !== null &&
      Math.abs(reading.offsetMs - toMillis(held.offsetMs)) > this.#divergenceToleranceMs
    ) {
      this.reset()
      // **여기서는 마지막 확정값까지 버린다.** 새 표본이 이만큼 어긋났다는 것은 두 시계
      // 중 하나가 실제로 움직였다는 뜻이고, 그러면 들고 있던 값은 낡은 것이 아니라
      // 거짓이다. 낡은 값은 흐리게 남겨도 되지만 거짓인 값은 그럴 수 없다.
      this.#lastEstimate = null
    }

    this.#trips.push(trip)
    if (this.#trips.length > this.#windowSize) this.#trips.shift()
    // **표본을 창에 넣은 자리에서 찍는다.** 전에는 전송이 돌아온 직후에 찍었는데, 그러면
    // 서버가 답은 하되 표본이 매번 거절되는 부류(중간 캐시가 옛 답을 재사용해 t0 이 안 맞는
    // 경우, 서버가 t2 를 왕복보다 크게 주장해 왕복이 음수인 경우)에서 5초 폴링이 매번 도장을
    // 새로 찍어 **「연결 없음」에 영영 못 간다.** 그 상태에서 화면은 몇 분 묵은 숫자를 띄운 채
    // 「측정 중 (0/3)」이라고 적고, 사람은 그것을 「곧 나온다」로 읽는다. 이 필드가 답해야 할
    // 물음은 「서버가 살아 있는가」가 아니라 **「사람에게 기다리라고 할 것인가」**다.
    this.#lastReachableElapsed = toMillis(this.#clock.elapsed())
    this.#lastEventLoopLagMs = response.eventLoopLagMs
    this.#lastSuccessElapsed = toMillis(this.#clock.elapsed())
    this.#mark = { wall: this.#clock.now(), elapsed: this.#clock.elapsed() }

    // **쓰는 자리는 여기 하나다.** 읽는 쪽(`estimate` 게터)에서 쌓으면 화면을 그리는 일이
    // 저장된 값을 바꾸는 일이 되고, 그 부작용은 언제 일어나는지 아무도 못 짚는다.
    const settled = this.estimate
    if (settled !== null) this.#lastEstimate = settled

    return reading
  }

  /**
   * 지금 믿어도 되는 표본들.
   *
   * **낡았으면 빈 목록이다.** 버리지 않고 빈 것으로 보는 이유는 읽기가 상태를 바꾸면 안
   * 되기 때문이고, 값이 다시 흐르기 시작하면 그 순간부터 다시 세는 편이 맞기 때문이다.
   */
  #usable(): readonly RoundTrip[] {
    return this.#snapshot().usable
  }

  /**
   * 한 시점의 읽기 상태 전부. **시계를 딱 한 번 읽는다.** 아무것도 안 바꾼다.
   *
   * 조각마다 시계를 새로 읽으면 안 되는 이유가 실측으로 나왔다. 점프 판정은 벽시계와
   * 단조 시계의 차를 허용 폭과 견주는데, 그 차가 폭 언저리에 앉아 있으면 인접한 두 문장
   * 사이에서 어느 한 축만 정수 경계를 넘어 판정이 갈린다. 그러면 같은 렌더 안에서 값은
   * 살아 있고 표본 수는 0 인 조합이 나오고, 화면이 **「측정 중 (6/3)」** 같은 말을 한다.
   * 그 문자열은 이 파일이 스스로 금지해 둔 것이다.
   *
   * **읽기가 상태를 안 바꾼다는 성질은 그대로다.** 두 축이 이미 어긋났으면 왕복을
   * 기다리지 않고 빈 목록을 내되, 표본을 실제로 버리는 것은 다음 왕복의 일이다. 화면을
   * 그리는 일이 저장된 값을 바꾸면 그 부작용은 언제 일어나는지 아무도 못 짚는다.
   *
   * 이 성질이 「시계가 튄 뒤 최대 한 왕복 주기(5초) 동안 확신에 찬 오답」을 닫는다.
   * 2026-08-17 2회차 관측이 그 구간을 4.5~4.7초로 쟀다. 더 자주 **묻는** 대신 **읽을 때
   * 보는** 쪽을 골랐고, 그래서 화면은 다음 렌더에 이미 안다.
   */
  #snapshot(): { usable: readonly RoundTrip[]; drifted: boolean; reachable: boolean } {
    const now: ClockMark = { wall: this.#clock.now(), elapsed: this.#clock.elapsed() }
    const elapsedMs = toMillis(now.elapsed)

    const gapNow = toMillis(continuityGapMs(this.#mark, now))
    const drifted =
      Math.abs(gapNow) > this.#resyncToleranceMs ||
      Math.abs(this.#heldJumpSum(elapsedMs) + gapNow) > this.#resyncToleranceMs
    const stale =
      this.#lastSuccessElapsed !== null &&
      elapsedMs - this.#lastSuccessElapsed > this.#staleAfterMs

    // **단조 축 위에서만 잰다.** 벽시계로 재면 두 방향으로 다 틀린다. 화면이 20초 꺼졌다
    // 켜지면 잔 시간이 「못 붙은 시간」으로 세어져 멀쩡한 연결에 「연결 없음」이 뜨고,
    // 반대로 NTP 가 벽시계를 뒤로 당기면 차가 음수라 왕복이 죽어도 영영 참이 된다.
    const base = this.#lastReachableElapsed ?? this.#startedElapsed

    return {
      usable: drifted || stale ? [] : this.#trips,
      drifted,
      reachable: elapsedMs - base <= this.#offlineAfterMs,
    }
  }

  /** 마지막 표시 이후 벽시계가 단조 시계보다 허용 폭 넘게 갔는가. **아무것도 안 바꾼다.** */
  #drifted(): boolean {
    return this.#snapshot().drifted
  }

  get estimate(): OffsetEstimate | null {
    return this.#estimateFrom(this.#snapshot().usable)
  }

  #estimateFrom(usable: readonly RoundTrip[]): OffsetEstimate | null {
    if (usable.length < this.#minSamples) return null
    return estimateOffset(usable, this.#keepBest)
  }

  /**
   * 마지막으로 확정됐던 추정. **다시 재는 동안 화면이 흐리게 붙여 두는 값이다.**
   *
   * `estimate` 와 가르는 선이 「지금 이 값을 주장하는가」다. 그쪽은 주장이라 표본이
   * 모자라거나 낡으면 입을 다물고, 이쪽은 주장이 아니라 **마지막으로 안 것**이라 남는다.
   * 화면이 둘을 다른 밝기로 그려서 그 차이를 사람이 눈으로 본다.
   *
   * **점프는 왕복을 기다리지 않고 읽는 순간에 본다.** `#usable()` 이 그렇게 하는 것과
   * 같은 이유다. 다음 왕복까지 최대 5초가 남아 있는데, 그동안 이 값을 계속 내놓으면
   * 2026-08-27 결정이 「이때만은 지운다」고 지목한 바로 그 구간이 그대로 열려 있게 된다.
   */
  get heldEstimate(): OffsetEstimate | null {
    if (this.#drifted()) return null
    return this.#lastEstimate
  }

  /**
   * 지금 서버에 닿는가. 마지막 성공 왕복(한 번도 없었으면 이 인스턴스가 생긴 시각)부터
   * 문턱을 안 넘었으면 참이다. **표본이 쓸 만한지와 무관하다.** 묻는 것이 「이 값을
   * 믿어도 되는가」가 아니라 「사람에게 기다리라고 할 것인가 연결을 보라고 할 것인가」다.
   */
  get reachable(): boolean {
    return this.#snapshot().reachable
  }

  /**
   * 화면이 편차 칸 하나를 그리는 데 필요한 전부를 **한 번에** 읽는다.
   *
   * 스냅숏을 하나만 뜨는 것이 이 함수의 전부다. 조각마다 게터를 부르면 게터마다 시계를
   * 새로 읽고, 점프 판정이 허용 폭 언저리에 앉아 있을 때 조각끼리 답이 갈린다. 그러면
   * 값은 살아 있는데 표본 수가 0 인 조합이 나와 화면이 「측정 중 (6/3)」이라고 적는다.
   * 그리고 화면이 다섯이라(진입, 열람 둘, 구성, 계측) 규칙을 화면마다 다시 쓰면 반드시
   * 한 곳이 뒤처진다.
   */
  skewView(): SkewView {
    const snap = this.#snapshot()
    const fresh = this.#estimateFrom(snap.usable)
    const shown = fresh ?? (snap.drifted ? null : this.#lastEstimate)
    // **못 재는 중이 살아 있음을 이긴다.** 문턱을 넘도록 서버가 답을 안 했으면, 창에
    // 아직 안 낡은 표본이 남아 있어도 그 값은 지금 것이 아니다. 순서를 뒤집으면 나가는
    // 길이 막힌 뒤에도 낡음 문턱(30초)까지는 화면이 아무 일 없다는 얼굴을 한다.
    const state: SkewState = !snap.reachable ? 'offline' : fresh === null ? 'measuring' : 'live'
    return {
      millis: shown === null ? null : toMillis(shown.offsetMs),
      state,
      progress: `${String(snap.usable.length)}/${String(this.#minSamples)}`,
    }
  }

  /** 지금 창에 모여 있는 표본 수. 화면이 「측정 중 (2/3)」 같은 진척을 적는 데 쓴다. */
  get heldSamples(): number {
    // **낡은 표본은 세지 않는다.** 세면 화면이 「측정 중 (8/3)」 같은 말을 하게 되고, 그
    // 숫자는 사람에게 「곧 나온다」로 읽힌다 — 실제로는 재는 일이 멈춘 상태다.
    return this.#usable().length
  }

  /** 값을 내놓기 전에 모아야 하는 표본 수. */
  get minSamples(): number {
    return this.#minSamples
  }

  /** 서버가 마지막으로 알려 준 자기 이벤트 루프 지연. 화면이 아니라 원장이 보는 값이다. */
  get lastEventLoopLagMs(): number | null {
    return this.#lastEventLoopLagMs
  }

  /**
   * 지금 시각을 서버 시계 위에서 읽는다. 아직 동기되지 않았으면 주장하지 않는다.
   *
   * **재는 일이 멈췄으면 마지막 확정 편차로 읽는다** (배포 전 보수 회차, 2026-10-07). 전에는 왕복이
   * 30초 넘게 실패하면 null 을 냈고, 화면은 그 자리에 기기 시계를 썼다. 기기가 서버와 X 초 어긋난 사람은
   * 그 순간 카운트다운이 X 초 튀고 걸려 있던 개시 소리도 X 초 옮겨 갔다. 낡은 것은 틀린 것이 아니라는
   * 2026-08-27 결정을 여기에도 건다. **점프로 버린 뒤에만 주장하지 않는다.** `heldEstimate` 가 그 판정을
   * 읽는 순간에 한다.
   */
  serverNow(): Instant | null {
    const estimate = this.estimate ?? this.heldEstimate
    if (estimate === null) return null
    return addDuration(this.#clock.now(), estimate.offsetMs)
  }

  checkContinuity(): ContinuityCheck {
    const now: ClockMark = { wall: this.#clock.now(), elapsed: this.#clock.elapsed() }
    const gapMs = continuityGapMs(this.#mark, now)
    this.#mark = now

    const elapsedMs = toMillis(now.elapsed)
    const gap = toMillis(gapMs)
    const discontinuous =
      Math.abs(gap) > this.#resyncToleranceMs ||
      Math.abs(this.#heldJumpSum(elapsedMs) + gap) > this.#resyncToleranceMs
    if (discontinuous) {
      this.reset()
      // 기기 시계가 뛰었다. 들고 있던 값은 낡은 것이 아니라 거짓이 됐으므로 여기서만
      // 함께 버린다 (2026-08-27 사용자 결정의 「지우는 것이 맞는 경우」). 폭 안의 점프가
      // 쌓여 폭을 넘은 경우도 같다. 화면 값이 이미 그 합만큼 틀려 있다.
      this.#lastEstimate = null
    } else if (gap !== 0) {
      // 오래된 기록은 쓰는 자리에서만 걷는다. 읽는 자리(`#snapshot`)는 상태를 바꾸지 않는다.
      const horizon = elapsedMs - this.#windowSize * 5_000
      this.#recentJumps = this.#recentJumps.filter((jump) => jump.elapsedMs >= horizon)
      this.#recentJumps.push({ elapsedMs, gapMs: gap })
    }
    return { discontinuous, gapMs }
  }

  /**
   * 아직 표본 창에 들어가지 않았을 수 있는 점프의 합. **읽기만 한다.**
   *
   * 창의 길이(표본 수 곱하기 정상 왕복 주기 5초)보다 오래된 점프는 세지 않는다. 그 뒤의 표본은
   * 이미 점프한 시계로 쟀으므로 화면 값에 들어가 있다. 오래된 것까지 세면 이 머신처럼 벽시계가
   * 30초마다 수십 ms 씩 뛰는 기기에서 몇 분마다 멀쩡한 화면에 「측정 중」이 뜬다.
   */
  #heldJumpSum(elapsedMs: number): number {
    const horizon = elapsedMs - this.#windowSize * 5_000
    return this.#recentJumps
      .filter((jump) => jump.elapsedMs >= horizon)
      .reduce((sum, jump) => sum + jump.gapMs, 0)
  }

  /**
   * **모아 둔 표본을 버린다.** 다음 probe 부터 처음처럼 다시 수렴한다.
   *
   * **마지막 확정값은 건드리지 않는다.** 세 자리에서 불리는데 셋의 뜻이 같지 않기
   * 때문이다. 낡은 창을 되살리지 않는 자리는 「재는 일이 멈췄었다」는 뜻이지 「값이
   * 틀렸다」는 뜻이 아니고, 거기서까지 값을 버리면 연결이 돌아오는 바로 그 순간에 화면의
   * 숫자가 사라지는 거꾸로 된 동작이 된다. 값이 거짓이 된 두 자리(시계 점프, 상대 시계
   * 이동)는 각자 그 자리에서 명시적으로 버린다.
   */
  reset(): void {
    // 새로 모을 표본은 이미 뛴 시계로 재므로, 그 전의 점프는 더는 화면 값을 틀리게 하지 않는다.
    this.#recentJumps = []
    this.#trips = []
    this.#lastEventLoopLagMs = null
    this.#lastSuccessElapsed = null
  }
}

/**
 * 실제 서버로 가는 왕복. 경로를 여기에 적지 않고 계약에서 읽는 이유는, 경로가 두 군데
 * 적히면 한쪽만 고쳐지는 날이 반드시 오기 때문이다.
 *
 * **왕복에는 시간 상한이 있다(기본 5초, 평소 폴링 간격과 같다).** 폴링은 앞 왕복이 끝나야
 * 다음 왕복을 예약하므로, 답도 오류도 없이 매달린 요청 하나가 재는 일 전체를 세운다. 브라우저
 * fetch 에는 기본 상한이 없다. 상한을 폴링 간격에 맞추면 「15초 = 세 번 연속 놓침」이 매달린
 * 요청에서도 그대로 성립한다. 5초 넘게 걸린 왕복은 편차 오차가 그 절반까지 커서 어차피 쓸 표본이
 * 아니다. 상한으로 끝난 왕복은 다른 실패와 같이 표본 하나를 놓친 것으로 센다.
 */
export function httpTransport(baseUrl = '', timeoutMs = 5_000): ClockSyncTransport {
  const url = `${baseUrl}${routes.clockSync.path}`
  return async (t0: number): Promise<ClockSyncResponse> => {
    const abort = new AbortController()
    const timer = setTimeout(() => abort.abort(), timeoutMs)
    try {
      const response = await fetch(url, {
        method: routes.clockSync.method,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ t0 }),
        // 중간 캐시가 답을 재사용하면 t1, t2 가 과거 값이 되어 편차가 통째로 거짓이 된다.
        cache: 'no-store',
        signal: abort.signal,
      })
      if (!response.ok) {
        throw new Error(`시계 동기 실패: HTTP ${String(response.status)}`)
      }
      return (await response.json()) as ClockSyncResponse
    } finally {
      clearTimeout(timer)
    }
  }
}
